// src/pages/NinVerification.js
import React, { useState } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link,
} from "@mui/material";
import { Person, Badge, Info, ArrowBack } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { postData } from "../services/request";
import logo from "../assets/logo/logo3.png";

const labelSx = {
  display: "block",
  color: "#52057b",
  fontWeight: 600,
  fontSize: "0.9rem",
  mb: 1,
};

const inputSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#fff",
    boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0px 4px 12px rgba(82, 5, 123, 0.1)",
    },
    "&.Mui-focused": {
      boxShadow: "0px 4px 15px rgba(82, 5, 123, 0.2)",
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e0e0e0",
  },
  "& .MuiInputBase-input": {
    py: 1.8,
  },
};

const NinVerification = () => {
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [nin, setNin] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleContinue = async () => {
    if (!firstName || !lastName || !nin) {
      setSnackbar({
        open: true,
        message: "Please fill all fields",
        severity: "warning",
      });
      return;
    }

    if (nin.length !== 11) {
      setSnackbar({
        open: true,
        message: "NIN must be 11 digits long",
        severity: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      const storedUserData = localStorage.getItem("userData");
      const userData = storedUserData ? JSON.parse(storedUserData) : null;
      const email = userData?.email;

      if (!email) {
        setSnackbar({
          open: true,
          message: "User email is missing. Please log in again.",
          severity: "error",
        });
        return;
      }

      const payload = {
        nin: nin,
        firstname: firstName,
        lastname: lastName,
        email: email,
      };

      const response = await postData("/api/auth/nin-verification", payload);

      if (response.status === true) {
        setSnackbar({
          open: true,
          message: "NIN Verified Successfully!",
          severity: "success",
        });
        setTimeout(() => navigate("/identity-verification"), 1500);
      } else {
        setSnackbar({
          open: true,
          message: response.message || "Verification failed. Please try again.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: "Network error. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #f5f0ff 0%, #ffffff 100%)",
        py: 1,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: { xs: "90%", sm: 450 },
          borderRadius: "24px",
          overflow: "hidden",
          background: "#fff",
          boxShadow: "0px 20px 60px rgba(82, 5, 123, 0.15)",
        }}
      >
        {/* Purple Header Box */}
        <Box
          sx={{
            backgroundColor: "#52057b",
            p: 4,
            textAlign: "center",
            color: "#fff",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <img src={logo} alt="Dajoo" style={{ width: "120px" }} />
          </Box>
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
            National ID Verification
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Secure your account with your NIN
          </Typography>
        </Box>

        {/* Form Section */}
        <Box sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography
            sx={{
              color: "#52057b",
              fontWeight: "bold",
              fontSize: "1.2rem",
              mb: 1,
            }}
          >
            Hello 👋
          </Typography>
          <Typography variant="body1" sx={{ color: "#666", mb: 3 }}>
            We use NIN to ensure your account belong to you.
          </Typography>

          <Box component="form">
            {/* First Name */}
            <Typography sx={labelSx}>First Name</Typography>
            <TextField
              fullWidth
              variant="outlined"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              sx={inputSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person sx={{ color: "#52057b", fontSize: "1.2rem" }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Last Name */}
            <Typography sx={{ ...labelSx, mt: 3 }}>Last Name</Typography>
            <TextField
              fullWidth
              variant="outlined"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              sx={inputSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person sx={{ color: "#52057b", fontSize: "1.2rem" }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* NIN Input */}
            <Typography sx={{ ...labelSx, mt: 3 }}>
              National Identification Number (NIN)
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              value={nin}
              onChange={(e) =>
                setNin(e.target.value.replace(/[^0-9]/g, "").slice(0, 11))
              }
              inputProps={{
                maxLength: 11,
                style: { fontSize: "1.1rem", letterSpacing: "2px" },
              }}
              sx={inputSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Badge sx={{ color: "#52057b" }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Info Box */}
            <Box
              sx={{
                mt: 3,
                backgroundColor: "#f5f0ff",
                p: 2,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Info sx={{ color: "#52057b", fontSize: "1.3rem" }} />
              <Typography
                variant="body2"
                sx={{ color: "#52057b", fontWeight: 500 }}
              >
                We use NIN to uniquely identify you and secure your account.
              </Typography>
            </Box>

            {/* Continue Button */}
            <Button
              fullWidth
              variant="contained"
              onClick={handleContinue}
              disabled={loading}
              sx={{
                mt: 4,
                bgcolor: "#52057b",
                color: "#fff",
                py: 2,
                borderRadius: "12px",
                fontWeight: "bold",
                fontSize: "1.1rem",
                textTransform: "none",
                boxShadow: "0px 10px 25px rgba(82, 5, 123, 0.3)",
                "&:hover": {
                  bgcolor: "#3e055e",
                  boxShadow: "0px 15px 35px rgba(82, 5, 123, 0.4)",
                  transform: "translateY(-2px)",
                },
                "&.Mui-disabled": {
                  bgcolor: "#e0e0e0",
                  color: "#9e9e9e",
                  boxShadow: "none",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "#fff" }} />
              ) : (
                "Continue"
              )}
            </Button>

            <Typography
              variant="body2"
              sx={{ textAlign: "center", mt: 3, color: "#666" }}
            >
              <Link
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setSnackbar({
                    open: true,
                    message:
                      "Why NIN? It helps us prevent fraud and ensure only real users join.",
                    severity: "info",
                  });
                }}
                sx={{
                  color: "#52057b",
                  fontWeight: "600",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                See why we need your NIN?
              </Link>
            </Typography>
          </Box>
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity={snackbar.severity}
            sx={{ width: "100%", borderRadius: "12px" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
};

export default NinVerification;
